import type { FeatureCollection } from "@turf/helpers";
import type { Map as MapType } from "mapbox-gl";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Map, MapPlaceholder } from "senf-atomic-design-system";
import { flyToPolygon } from "senf-atomic-design-system/src/components/atoms/map/utils/flyToPolygon";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import type { GISData } from "./Map.types";

type MapSettings = {
  center: [number, number];
  pitch: number;
  bearing: number;
  zoom?: number;
  height?: number;
};

type MapViewEditorProps = {
  children: React.ReactNode;
  mapSettings: MapSettings;
  shouldFlyTo: MapSettings;
  setMapSettings: (mapSettings: MapSettings | null) => void;
  accessibilityInfotext: string;
  allowFullscreen: boolean;
  mapData?: {
    mapbounds: FeatureCollection;
    mapStyle: string;
    gisData: GISData;
  };
  resizeDependencies: string[];
  flyToDependencies: string[];
  mode: string;
  statefulMap: MapType;
  setStatefulMap: React.Dispatch<React.SetStateAction<MapType>>;
  setFullscreenMap: (fullscreenMap: boolean) => void;
  fullscreenMap: boolean;
  setShowMapDataSetList: React.Dispatch<React.SetStateAction<boolean>>;
};

const MapViewEditor = ({
  children,
  mapSettings,
  setMapSettings,
  accessibilityInfotext,
  mapData,
  allowFullscreen,
  resizeDependencies,
  flyToDependencies,
  mode,
  statefulMap,
  setStatefulMap,
  setFullscreenMap,
  fullscreenMap,
  setShowMapDataSetList,
  shouldFlyTo,
  shouldTriggerRefresh,
}: MapViewEditorProps) => {
  const { t } = useTranslation();
  const [mapSettingsDefined, setMapSettingsDefined] = useState(false);
  const [hasFlownToPolygon, setHasFlownToPolygon] = useState(false);

  const [cesiumCameraPosition, setCesiumCameraPosition] = useState<{
    center: [number, number];
    pitch: number;
    bearing: number;
    height: number;
  } | null>(null);

  // Check if we're using Cesium map
  const isCesiumMap =
    mapData?.mapStyle === "cesium" || mapData?.mapStyle === "cesium-rendering";

  useEffect(() => {
    if (isCesiumMap && mapSettings?.center?.[0]) {
      // Fix the type mismatch by creating a properly typed object
      setCesiumCameraPosition({
        center: [mapSettings?.center?.[0] || 0, mapSettings?.center?.[1] || 0],
        pitch: mapSettings?.pitch || 0,
        bearing: mapSettings?.bearing || 0,
        height: mapSettings?.height || 0,
      });

      setMapSettingsDefined(true);
    }
  }, [mapSettings, isCesiumMap]);

  useEffect(() => {
    if (mapSettings && statefulMap && !isCesiumMap) {
      setMapSettingsDefined(true);

      if (!hasFlownToPolygon) {
        flyToPolygon(statefulMap, mapData?.mapbounds, 0);
        setHasFlownToPolygon(true);
      }


      setTimeout(() => {
        statefulMap?.flyTo({
          center: mapSettings?.center,
          zoom: mapSettings?.zoom,
          duration: 2300,
          pitch: mapSettings?.pitch || 0,
          bearing: mapSettings?.bearing || 0,
        });
      }, 50);
    }
  }, [
    statefulMap,
    mapSettings,
    mapData?.mapbounds,
    isCesiumMap,
    shouldFlyTo,
    shouldTriggerRefresh,
  ]);

  const events = ["zoom", "drag", "click", "rotate", "pitch"];
  const addEvent = useCallback(
    (eventName: string, handler: (e: unknown) => void) =>
      statefulMap?.on(eventName, handler),
    [statefulMap],
  );
  const removeEvent = useCallback(
    (eventName: string, handler: (e: unknown) => void) =>
      statefulMap?.off(eventName, handler),
    [statefulMap],
  );

  useEffect(() => {
    const handleInteraction = (e) => {
      if (e.originalEvent) {
        setMapSettingsDefined(false);
        for (const event of events) {
          removeEvent(event, handleInteraction);
        }
      }
    };

    if (mapSettingsDefined && !isCesiumMap) {
      for (const event of events) {
        addEvent(event, handleInteraction);
      }
    }

    return () => {
      for (const event of events) {
        removeEvent(event, handleInteraction);
      }
    };
  }, [mapSettingsDefined, addEvent, removeEvent, isCesiumMap]);

  const handleSetView = () => {
    console.log("handleSetView", isCesiumMap, cesiumCameraPosition);
    if (isCesiumMap && cesiumCameraPosition) {
      setMapSettings({
        pitch: cesiumCameraPosition?.pitch || 0,
        bearing: cesiumCameraPosition?.bearing || 0,
        center: cesiumCameraPosition?.center || [0, 0],
        height: cesiumCameraPosition?.height || 0,
      });
    } else if (statefulMap) {
      setMapSettings({
        zoom: statefulMap?.getZoom(),
        pitch: statefulMap?.getPitch(),
        bearing: statefulMap?.getBearing(),
        center: [statefulMap?.getCenter().lng, statefulMap?.getCenter().lat],
      });
    }
    setMapSettingsDefined(true);
  };

  const handleReset = () => {
    setMapSettings(null);
    setMapSettingsDefined(false);

    if (!isCesiumMap && statefulMap) {
      flyToPolygon(statefulMap, mapData?.mapbounds, 1000);
    }
  };

  return (
    <>
      {mode === "edit" && !mapData ? (
        <>
          <MapPlaceholder
            rounded={false}
            onClick={() => setShowMapDataSetList(true)}
          />
        </>
      ) : (
        <div className="flex h-full w-full justify-center">
          {allowFullscreen && (
            <Button
              variant="white"
              leadingIcon={
                fullscreenMap ? "arrowhead-down-lg" : "arrowhead-up-lg"
              }
              onClick={() => setFullscreenMap(!fullscreenMap)}
              text={
                fullscreenMap
                  ? t("util:show", { val: t("global:question") })
                  : t("util:expand", { val: t("global:map") })
              }
              className="shadow-xl"
              classNameWrapper="absolute z-50 m-4"
            />
          )}
          <div className="h-full w-full">
            <Map
              id="map-survey"
              key={mode}
              statefulMap={statefulMap}
              setStatefulMap={setStatefulMap}
              optimizeString={true}
              resize={resizeDependencies?.toString()}
              mapbounds={mapData?.mapbounds}
              mapStyle={mapData?.mapStyle}
              flyToMapbounds={false}
              gisData={mapData?.gisData}
              accessibilityInfotext={accessibilityInfotext}
              onCesiumCameraMove={(e) => {
                console.log("onCesiumCameraMove", e);
                setCesiumCameraPosition(e);
              }}
              cesiumCameraPosition={cesiumCameraPosition}
              shouldFlyTo={shouldFlyTo}
            />
            {mode === "edit" && (
              <>
                <div className="z-5 absolute right-4 top-4 flex gap-1">
                  {!mapSettingsDefined && (
                    <Button
                      leadingIcon="lock-view"
                      text={t("map:builder.setView")}
                      variant="white"
                      className="shadow-lg"
                      onClick={handleSetView}
                    />
                  )}
                  {mapSettings && (
                    <Button
                      leadingIcon="reload"
                      text={t("global:reset")}
                      variant="white"
                      className="shadow-lg"
                      onClick={handleReset}
                    />
                  )}
                </div>
                {mapSettings && mapSettingsDefined && (
                  <div className="ring-border-highlight-default pointer-events-none absolute top-0 h-full w-full rounded-l-md ring-2 ring-inset">
                    <div className="absolute left-3 top-3">
                      <Icon
                        name="lock-view"
                        className="text-border-highlight-default icon-border"
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {children}
    </>
  );
};

export default MapViewEditor;
